import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Eye, EyeOff, RefreshCw, Zap, Sun, Moon, ArrowRight,
  CheckCircle2, Users, ShieldCheck, TrendingUp, Sparkles, Lock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSTNAuth } from '@/context/STNAuthContext';
import { useTheme } from '@/context/ThemeContext';
import { toast } from 'sonner';

const FEATURES = [
  { icon: Users,       label: 'Hire AI-matched interns',          desc: 'From 500+ colleges across India' },
  { icon: ShieldCheck, label: 'Instant credential verification',   desc: 'CIN, GST & PAN verified in seconds' },
  { icon: TrendingUp,  label: 'Mentor & investor network',         desc: 'Get funded, guided, and scaled' },
  { icon: Sparkles,    label: 'PromptNexus AI training',           desc: 'Upskill your team with AI-powered courses' },
];

export default function Login() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { login, isAuthenticated, startup } = useSTNAuth();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [loading, setLoading]   = useState(false);

  if (isAuthenticated) {
    const s = startup?.status;
    if (s === 'verified')                             { navigate('/dashboard',    { replace: true }); return null; }
    if (s === 'pending_review' || s === 'rejected')   { navigate('/under-review', { replace: true }); return null; }
    navigate('/onboarding', { replace: true }); return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Email and password are required'); return; }
    setLoading(true);
    try {
      const { startup: s } = await login({ email, password });
      toast.success('Welcome back!');
      const status = s?.status;
      if (status === 'verified')                              navigate('/dashboard',    { replace: true });
      else if (status === 'pending_review' || status === 'rejected') navigate('/under-review', { replace: true });
      else                                                    navigate('/onboarding',   { replace: true });
    } catch (err) {
      toast.error(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">

      {/* ── Left branding panel ──────────────────────────────────────────────── */}
      <div
        className="hidden lg:flex flex-col w-[400px] xl:w-[440px] flex-shrink-0 relative overflow-hidden"
        style={{ background: 'linear-gradient(150deg, #1e0a3c 0%, #160d2e 40%, #0d0a1f 100%)' }}
      >
        {/* Subtle glow orbs */}
        <div className="absolute top-0 left-0 w-64 h-64 rounded-full opacity-20 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #7c3aed, transparent)', transform: 'translate(-30%, -30%)' }} />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #4f46e5, transparent)', transform: 'translate(30%, 30%)' }} />

        <div className="relative z-10 flex flex-col h-full p-9">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 mb-12">
            <div className="w-9 h-9 rounded-xl bg-violet-600 flex items-center justify-center shadow-lg shadow-violet-900/50">
              <Zap className="w-4.5 h-4.5 text-white" />
            </div>
            <span className="font-extrabold text-white text-base tracking-tight">StartupNexus</span>
          </Link>

          {/* Headline */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="mb-2">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-violet-400 mb-3">
                India's Startup Platform
              </span>
              <h2 className="text-3xl font-extrabold text-white leading-tight mb-4">
                Everything your<br />startup needs,<br />
                <span className="text-violet-400">in one place.</span>
              </h2>
              <p className="text-[15px] text-white/65 leading-relaxed">
                From your first intern to your Series A — StartupNexus connects you with the talent, mentors, and capital to grow.
              </p>
            </div>

            {/* Feature list */}
            <div className="mt-8 space-y-4">
              {FEATURES.map(f => (
                <div key={f.label} className="flex items-start gap-3.5">
                  <div className="w-8 h-8 rounded-lg bg-violet-600/25 border border-violet-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <f.icon className="w-4 h-4 text-violet-300" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white leading-snug">{f.label}</p>
                    <p className="text-xs text-white/50 mt-0.5">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trust footer */}
          <div className="mt-8 flex items-start gap-2.5 p-4 rounded-xl bg-white/5 border border-white/10">
            <Lock className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-white/60 leading-relaxed">
              <span className="text-white/90 font-medium">Bank-grade security.</span>{' '}
              Your data is end-to-end encrypted and credentials are cross-verified with MCA21, GSTN, and DPIIT databases.
            </p>
          </div>
        </div>
      </div>

      {/* ── Right form panel ─────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-h-screen">

        {/* Top bar */}
        <div className="flex items-center justify-between px-6 lg:px-10 py-5 border-b border-border">
          <Link to="/" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <span className="text-base leading-none">←</span> Back to home
          </Link>
          <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 flex items-center justify-center px-6 lg:px-12 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="w-full max-w-sm"
          >
            {/* Heading */}
            <div className="mb-8">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                <Zap className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground tracking-tight">Sign in</h1>
              <p className="text-sm text-muted-foreground mt-1.5">
                Welcome back — enter your credentials to continue.
              </p>
            </div>

            {/* Form fields */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-foreground">Email address</Label>
                <Input
                  type="email"
                  placeholder="you@startup.com"
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="h-10"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium text-foreground">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline font-medium">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    placeholder="Your password"
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="h-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full gradient-primary text-primary-foreground border-0 h-11 font-semibold text-sm gap-2 mt-1"
              >
                {loading
                  ? <><RefreshCw className="w-4 h-4 animate-spin" />Signing in…</>
                  : <>Sign In<ArrowRight className="w-4 h-4" /></>
                }
              </Button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">New here?</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Register CTA */}
            <Link
              to="/onboarding"
              className="flex items-center justify-center gap-2 w-full h-10 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-accent transition-colors"
            >
              <CheckCircle2 className="w-4 h-4 text-primary" />
              Create a free account
            </Link>

            <p className="text-xs text-center text-muted-foreground mt-5 leading-relaxed">
              By signing in you agree to our{' '}
              <span className="text-foreground/70 hover:underline cursor-pointer">Terms of Service</span>
              {' '}and{' '}
              <span className="text-foreground/70 hover:underline cursor-pointer">Privacy Policy</span>.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
